import { BindingEngine, computedFrom } from "aurelia-binding";
import { autoinject } from "aurelia-dependency-injection";
import { I18N } from "aurelia-i18n";
import * as Enumerable from "linq";
import * as moment from "moment";
import { Claims } from "../../../../../../../../constants/authentication/claims";
import { RightShipDialogServiceConstants } from "../../../../../../../../constants/basics/rightship-dialog.constants";
import { LookupValueQuery } from "../../../../../../../../constants/modules/lookup.constants";
import { MobileHelper } from "../../../../../../../../helpers/mobile-helper";
import { ChartDataModel } from "../../../../../../../../models/components/chart.models";
import { LookupValueRequest } from "../../../../../../../../models/lookups/lookup.models";
import { QueryFilter, DataGridColumn, DataType, SourceType } from "../../../../../../../../models/queries/queries.models";
import { GetGhgPerformance, VesselHelper } from "../../../../../../../../models/vessels/vessel.models";
import { UserClaimsService } from "../../../../../../../../services/authentication/user-claims.service";
import { UserClientService } from "../../../../../../../../services/authentication/user-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { LookupClientService } from "../../../../../../../../services/lookups/lookup-client.service";
import { CompanyService } from "../../../../../../../../services/modules/company-client.service";
import { QueriesClientService } from "../../../../../../../../services/queries/queries-client.services";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { InitFilterValueConverter } from "../../../../../../../components/converters/queries/init-filter";
import { QueryFiltersExecutor } from "../../../../../../../components/converters/queries/query-filter";
import { RightShipMultiLookupModels, MultiLookupModel } from "../../../../../../../components/multi-checked-list-selector/multi-checked-list-selector";
import { RatingItem } from "../../../../../../../components/rating-selector/rating-selector";
import { CustomGridPageBase } from "../../../../../../common/custom-grid-page.base";
import { GhgRatingSelector } from "../../../../../../../../views/components/ghg-rating-selector/ghg-rating-selector";
import { ManageRightShipUsersClientService } from "../../../../../../../../services/modules/righship-qi-manage-users.service";
import { SubscriptionProductCodes } from "../../../../../../../../models/customers/customer-subscription.models";
import { Reflection } from "../../../../../../../../utilities/reflection";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { useView } from "aurelia-framework";
const am4core = await import("@amcharts/amcharts4/core");
const am4charts = await import("@amcharts/amcharts4/charts");

@autoinject
@useView('views/pages/modules/company/tabs/environment/tabs/ghg-rating/company-ghg-rating.html')
export class CompanyGhgRating extends CustomGridPageBase {

    private _vesselIds: number[];
    private ratings: string[] = [];

    public isChartsDataLoading: boolean = true;
    public companyId: number = null;
    public ghgPerformanceData: GetGhgPerformance;
    public rightShipUser: boolean;
    public initialFilters: QueryFilter[];
    public canViewGHGFilter : boolean = false;
    public canViewSafetyFilter : boolean = false;
    public ghgEnergySavingDevicesChartDiv: HTMLDivElement;
    public ghgVerificationStatusChartDiv: HTMLDivElement;
    public fleetGhgRatingChartData: ChartDataModel;
    public ghgEnergySavingDevicesChartData: any[];
    public ghgVerificationStatusChartData: any[];
    public eediVesselTypeHighestCount: number;
    public ghgRatingSelector: GhgRatingSelector;
    public eediVesselTypes: { name: string, count: number }[];
    public isLimitedSubscription: boolean;
    public docVsWorldItems: { name: string, color: string }[] = 
        [{
            name: 'DOC Fleet',
            color: '#489D90'
        },
        {
            name: 'World Fleet',
            color: '#003745'
        }
        ];
    public docVsWorldFadedItems: { name: string, color: string }[] = 
        [{
            name: 'DOC Fleet',
            color: '#D1E7E3'
        },
        {
            name: 'World Fleet',
            color: '#BAC8CB'
        }
        ];


    constructor(i18N: I18N, protected bindingEngine: BindingEngine, userClaimsService: UserClaimsService, private lookupService: LookupClientService,
        private companyService: CompanyService, private dialogClientService: DialogClientService, private queriesClientService: QueriesClientService,
        private initFilterConverter: InitFilterValueConverter, private userClientService: UserClientService, private notificationClientService: NotificationClientService,
        private manageRightShipUsersClientService: ManageRightShipUsersClientService, private configService: ConfigService) {
        super(i18N, 'Vessels', bindingEngine);

        this.rightShipUser = userClaimsService.hasPermission(Claims.RightShipUser)
        this.canViewSafetyFilter = userClaimsService.hasPermission(Claims.ShowSafetyScore);
        this.canViewGHGFilter = userClaimsService.hasPermission(Claims.ShowGHG);
    }

    reloadCharts() {
        this.isChartsDataLoading = true;
        const initialFilters = this.initialFilters.filter(x => x.filterProperty && ((x.filterValue != null && x.filterValue != undefined) || !x.skipOnNullOrEmpty)
            && !(Array.isArray(x.filterValue) && x.filterValue.length == 0)
            && x.filterProperty.name != 'ghgRatingWeight');

        const queryFiltersExecutor = new QueryFiltersExecutor(this.initFilterConverter,
            initialFilters,
            null,
            null,
            null,
            this.userClientService.currentUser);

        // the odata url must be called again to get filtered data without pagination otherwise if you read from the datagrid directly the charts won't represent the entire dataset.
        queryFiltersExecutor.getUri(true, false, "odata/CompanyGhgVesselPerformances/Genesis.GetCompanyGhgVesselPerformances(companyId=" + this.companyId + ")")
            .then(uri => {

                this.queriesClientService.getData(uri).then(x => {
                    this._vesselIds = x.value.map(x => x.id);
                    this.populateChartData();
                }).catch(e => {
                    this.isChartsDataLoading = false;
                    console.error(e);
                    this.notificationClientService.show(this.i18N.tr('error-text'), 'Failed to load GHG performance chart data', NotificationType.Error, 5000);
                });
            }).catch(e => {
                this.isChartsDataLoading = false;
                console.error(e);
                this.notificationClientService.show(this.i18N.tr('error-text'), 'Failed to load GHG performance chart data', NotificationType.Error, 5000);
            });
    }

    populateChartData() {

        const request = {
            companyId: this.companyId,
            vesselIds: this._vesselIds,
            ghgRatingWeights: this.selectedGhgRatingWeights
        }

        this.companyService.getGhgPerformance(request).then(response => {

            this.ghgPerformanceData = response;

            const eediVesselTypes: {
                name: string,
                count: number
            }[] = [];

            this.ghgVerificationStatusChartData = [];
            this.ghgEnergySavingDevicesChartData = [];

            let fleetGhgRatingChartData: ChartDataModel = {
                chartDataEntries: [],
                labels: null
            };

            const allVesselsCount = response.ghgUnverifiedCount + response.ghgVerifiedCount;

            if (allVesselsCount > 0) {

                //DOC Fleet GHG Verification Status Chart
                const ghgVerifiedPercent = Number((100 * response.ghgVerifiedCount / allVesselsCount).toFixed(2));
                const ghgUnverifiedPercent = Number((100 * response.ghgUnverifiedCount / allVesselsCount).toFixed(2));

                this.ghgVerificationStatusChartData = [{
                    "category": "Verified",
                    "value": ghgVerifiedPercent,
                    "color": "#1A8574",
                    "formatData": response.ghgVerifiedCount.toString()
                }, {
                    "category": "Unverified",
                    "value": ghgUnverifiedPercent,
                    "color": "#B3B3B3",
                    "formatData": response.ghgUnverifiedCount.toString()
                }]

                this.pieChartBase(this.ghgVerificationStatusChartDiv, this.ghgVerificationStatusChartData);

                //Energy Saving Devices Chart
                const ghgWithEnergySavingDevicesPercent = Number((100 * response.ghgPlusCount / allVesselsCount).toFixed(2));
                const ghgWithoutEnergySavingDevicesPercent = Number((100 * response.ghgNotPlusCount / allVesselsCount).toFixed(2));

                this.ghgEnergySavingDevicesChartData = [{
                    "category": "With energy-saving devices",
                    "value": ghgWithEnergySavingDevicesPercent,
                    "color": "#006FF8",
                    "formatData": response.ghgPlusCount.toString()
                }, {
                    "category": "Without energy-saving devices",
                    "value": ghgWithoutEnergySavingDevicesPercent,
                    "color": "#B3B3B3",
                    "formatData": response.ghgNotPlusCount.toString()
                }]

                this.pieChartBase(this.ghgEnergySavingDevicesChartDiv, this.ghgEnergySavingDevicesChartData);
            }

            response.vesselTypeEEDIGroupCounts.forEach(item => {
                eediVesselTypes.push({
                    count: item.count,
                    name: item.name
                });
            });

            this.eediVesselTypes = eediVesselTypes;

            //Set highest count to be used for chart calculation
            this.eediVesselTypeHighestCount = Math.max(...this.eediVesselTypes.map(x => x.count));

            const docFleetGhgRatingCountsSum = Enumerable.from(response.ghgRatingCounts).sum(item => item.count);
            const worldFeeltGhgRatingCountsSum = Enumerable.from(response.worldFeeltGhgRatingCounts).sum(item => item.count);

            if(docFleetGhgRatingCountsSum === 0 && worldFeeltGhgRatingCountsSum === 0) {
                fleetGhgRatingChartData = null;
            } else {
                response.ghgRatingCounts.forEach(item => {
                    if (docFleetGhgRatingCountsSum === 0) {
                        return;
                    }
    
                    const value = Number((100 * item.count / docFleetGhgRatingCountsSum).toFixed(1));
                    let chartData = fleetGhgRatingChartData.chartDataEntries.filter(x => x.label == item.name)[0];
                    if (!chartData) {
                        chartData = {
                            data: [],
                            formatData: [],
                            label: item.name,
                            color: this.getColor(item.name)
                        }
                        fleetGhgRatingChartData.chartDataEntries.push(chartData);
                    }
                    (<string[]>chartData.formatData).push(value.toString() + "%");
                    (<number[]>chartData.data).push(value);
                });
    
                response.worldFeeltGhgRatingCounts.forEach(item => {
                    if (worldFeeltGhgRatingCountsSum === 0) {
                        return;
                    }
    
                    const value = Number((100 * item.count / worldFeeltGhgRatingCountsSum).toFixed(1));
                    let chartData = fleetGhgRatingChartData.chartDataEntries.filter(x => x.label == item.name)[0];
                    if (!chartData) {
                        chartData = {
                            data: [],
                            formatData: [],
                            label: item.name,
                            color: this.getColor(item.name)
                        }
                        fleetGhgRatingChartData.chartDataEntries.push(chartData);
                    }
                    (<string[]>chartData.formatData).push(value.toString() + "%");
                    (<number[]>chartData.data).push(value);
                });
            }

            this.fleetGhgRatingChartData = fleetGhgRatingChartData;
            this.isChartsDataLoading = false;
        });
    }

    pieChartBase(chartDiv: HTMLDivElement, data) {

        //Create chart instance
        am4core.addLicense(this.configService.config.settings.amChartsLicense);
        const chart = am4core.create(chartDiv, am4charts.PieChart);
        chart.data = data

        chart.innerRadius = am4core.percent(50);

        //create series
        const pieSeries = chart.series.push(new am4charts.PieSeries());
        pieSeries.dataFields.category = "category";
        pieSeries.dataFields.value = "value";
        pieSeries.labels.template.text = "{value.formatNumber('#.##')}%";
        pieSeries.labels.template.adapter.add("text", (text, target) => {
            if (target.dataItem && (target.dataItem.values.value.percent <= 5)) {
                return "";
            }
            else {
                return "{value.formatNumber('#.##')}%"
            }
        });

        pieSeries.slices.template.tooltipText = "";
        pieSeries.slices.template.propertyFields.fill = "color";

        pieSeries.slices.template.stroke = am4core.color("#ffffff");
        pieSeries.slices.template.strokeWidth = 2;
        pieSeries.slices.template.strokeOpacity = 1;

        //Place label inside chart
        pieSeries.alignLabels = false;
        pieSeries.labels.template.radius = -20;
        pieSeries.labels.template.fontSize = "12px";
        pieSeries.labels.template.fill = am4core.color("#ffffff");
        pieSeries.ticks.template.disabled = true;
        

        //Hover state
        const hover = pieSeries.slices.template.states.getKey("hover");
        hover.properties.scale = 1;

        //Active state
        const active = pieSeries.slices.template.states.getKey("active");
        active.properties.shiftRadius = 0;
    }

    async activate(model: { companyId: number }) {
        if (model.companyId) {
            this.companyId = model.companyId;
        }

        this.isLimitedSubscription = await this.manageRightShipUsersClientService.isLimitedCustomerSubscription(SubscriptionProductCodes.informationServices);

        // this.populateChartData();
        this.columns = [];

        this.columns.push(
            <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                value: '<div class="column">' +
                    '<a class="link" href="/vessel/${item.vesselId}">${item.name}</a>' +
                    '<span if.bind="item.internationalMaritimeOrganizationNumber">IMO ${item.internationalMaritimeOrganizationNumber}</span>' +
                    '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span>' +
                    '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span>' +
                    '</div>',
                name: "name",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "Name",
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null
                }
            },
            <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                value: '<comp-safety-score score.bind="item.safetyRating" inspection.bind="item"" safety-rating-indicative.bind="item.safetyRatingIndicative" safety-score-type.bind="item.safetyRatingType" incident-grace-periods.bind="getIncidentGracePeriods(item)"' +
                    'has-error.bind="item.hasError" is-condensed="true" unlock-feature-key="vessels" locked-feature.bind="lockedFeature" mask-data.bind="maskData(item.safetyRating)"></comp-safety-score>',
                name: "safetyRating",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "Safety Score",
                instancePerRow: true,
                sortOrder: 0,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null,
                    lockedFeature: this.isLimitedSubscription,
                    maskData: (safetyRating) => {
                        return safetyRating === undefined;
                    },
                    getIncidentGracePeriods: (item) => {
                        return item.incidentGracePeriodJsonData ? JSON.parse(item.incidentGracePeriodJsonData) : [];
                    }
                },
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "220px"
            },
            <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                value: '<comp-ghg-rating rating.bind="item.ghgRating" rating-plus.bind="item.isGhgRatingPlus" is-verified.bind="item.isGhgRatingVerified" unlock-feature-key="vessels" locked-feature.bind="lockedFeature" mask-data.bind="maskData(item.ghgRating)"></comp-ghg-rating>',
                name: "ghgRatingWeight",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "GHG Rating",
                instancePerRow: true,
                sortOrder: 0,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null,
                    lockedFeature: this.isLimitedSubscription,
                    maskData: (ghgRating) => {
                        return ghgRating === undefined;
                    }
                },
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "140px"
            },
            <DataGridColumn>{
                sortOrder: 1,
                columnOrder: 1,
                name: "ghgRatingSizeScore",
                dataType: DataType.Double,
                title: "Size Score",
                isSortable: true
            },
            <DataGridColumn>{
                sortOrder: 1,
                columnOrder: 1,
                name: "evdi",
                dataType: DataType.Double,
                title: "EEDI/EVDI",
                isSortable: true
            },
            <DataGridColumn>{
                dataType: DataType.Number,
                name: "dwt",
                title: "DWT",
                sortOrder: 2,
                columnOrder: 2,
                isHidden: false,
                isSortable: true,
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "60px"
            },
            <DataGridColumn>{
                sortOrder: 1,
                columnOrder: 1,
                name: "age",
                dataType: DataType.Number,
                title: "Age",
                isSortable: true,
                valueConverter: (value, item) => {
                    return VesselHelper.getAgeFromDate(item.dateOfBuild)
                }
            },
            <DataGridColumn>{
                sortOrder: 1,
                columnOrder: 1,
                name: "vesselTypeEEDIPeeringGroup",
                dataType: DataType.Default,
                title: "EEDI Vessel Type",
                isSortable: true
            },
            <DataGridColumn>{
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.Default,
                isSortable: true,
                name: 'shipStatus',
                title: 'Vessel Status'
            });

        this.setDefaultFilters();
    }

    private readonly defaultStatusGroupFilters: string[] = ["in service"];

    private setDefaultFilters() {
        this.lookupService.getLookup(new LookupValueRequest(LookupValueQuery.VesselStatusGroups)).then(x => {

            const values = x.activeValues;

            if (values && values.length) {
                const lookup = LookupValueQuery.VesselStatuses;
                const ids = values.filter(v => this.defaultStatusGroupFilters.indexOf(v.name.trim().toLowerCase()) > -1).map(v => v.id);
                RightShipMultiLookupModels.setSelectedInMultiLookupByIds(LookupValueQuery.VesselStatuses, this.lookupService, ids, LookupValueQuery.VesselStatusGroups)
                    .then(res => {
                        this.mclFilters[lookup] = res.filters;
                        this.vesselStatusDisplayText = this.i18N.tr('vessel-status-text') + ' ' + res.filterDisplayText.trim();
                        this.vesselStatusDescription = res.filterDescription;
                        this.reloadFilters();
                    });
            }
        }).catch(e => {
            this.reloadFilters();
            console.error(e);
        });
    }

    onGhgRatingVerifiedOptionsReady($event) {

        const options = $event.detail.options;

        options.innerRadius = "90%";
        options.showBalloon = false;
        options.labelsEnabled = false;
        options.balloonText = "";
        options.labelText = "";
    }


    onGhgRatingOptionsReady($event) {
        const options = $event.detail.options;

        options.columnWidth = 0.6;

        options.addClassNames = true;
        if (!options.categoryAxis) {
            options.categoryAxis = {};
        }
        options.categoryAxis.gridThickness = 0;
        options.categoryAxis.labelRotation = 0;

        if (!options.valueAxes) {
            options.valueAxes = [{}];
        }
        options.valueAxes[0].gridThickness = 0;
        options.valueAxes[0].maximum = 100;
        options.graphs.forEach(graph => {
            //graph.fixedColumnWidth = 50;
            graph.labelFunction = (item) => {
                return item.dataContext[item.graph.valueField] + "%";
            };
        })
    }

    getBuilt(dateOfBuilt?: Date): string {
        if (!dateOfBuilt) return '';

        const date = moment(dateOfBuilt);
        const yearAge = VesselHelper.getAgeFromDate(date);
        return yearAge;
    }

    public reloadFilters() {
        const initialFilters = [];


        //#region safety rating filter

        if (this.selectedSafetyRatings && this.selectedSafetyRatings.length > 0) {
            const safetyRatingFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "safetyRating",
                    isDynamicProperty: false,
                    type: DataType.Lookup,
                    lookupMappedToProperty: "RS_PscInspectionStatuses",
                    isNullable: true,
                },
                filterValue: this.selectedSafetyRatings || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(safetyRatingFilter);
        }

        //#endregion

        //#region ghg values

        if (this.selectedGhgRatingWeights && this.selectedGhgRatingWeights.length > 0) {

            const ghgRatingFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "ghgRatingWeight",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedGhgRatingWeights || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(ghgRatingFilter);
        }

        //#endregion

        //#region vessel type EEDI peering groups values

        if (this.selectedVesselTypeEEDIPeeringGroups && this.selectedVesselTypeEEDIPeeringGroups.length > 0) {

            const selectVesselTypesFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "vesselTypeEEDIPeeringGroupId",
                    isDynamicProperty: false,
                    type: DataType.Lookup,
                    isNullable: true,
                    lookupMappedToProperty: "RS_VesselTypeEEDIPeeringGroups"
                },
                filterValue: this.selectedVesselTypeEEDIPeeringGroups || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(selectVesselTypesFilter);
        }

        //#endregion

        //#region dwt values

        if (this.dwtFirstFilter || this.dwtSecondFilter) {
            initialFilters.push(QueryFilter.getQuery('dwt', this.dwtFirstFilter ? this.dwtFirstFilter : 0, 'ge', DataType.Number, true));
            initialFilters.push(QueryFilter.getQuery('dwt', this.dwtSecondFilter ? this.dwtSecondFilter : 0, 'le', DataType.Number, true));
        }

        //#endregion

        //#region age values

        if (this.ageFirstFilter || this.ageSecondFilter) {
            initialFilters.push(QueryFilter.getQuery('age', this.ageFirstFilter ? this.ageFirstFilter : 0, 'ge', DataType.Number, true));
            initialFilters.push(QueryFilter.getQuery('age', this.ageSecondFilter ? this.ageSecondFilter : 0, 'le', DataType.Number, true));
        }

        //#endregion

        //#region vessel status values
        if (this.vesselStatusFilterActive) {
            this.mclFilters[LookupValueQuery.VesselStatuses].forEach(vtf => {
                initialFilters.push(vtf);
            });
        }

        //#endregion

        this.initialFilters = initialFilters;
        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: "odata/CompanyGhgVesselPerformances/Genesis.GetCompanyGhgVesselPerformances(companyId=" + this.companyId + ")",
            columns: this.columns,
            initialFilters: initialFilters
        };

        this.reloadCharts();
    }

    public closeAllFilters() {
        this.showSafetyRatingFilter = false;
        this.showGhgRatingFilter = false;
        this.showVesselTypeEEDIPeeringGroupFilter = false;
        this.showAgeFilter = false;
        this.showDwtFilter = false;
    }

    public clearAllFilters() {
        this.selectedSafetyRatings = null;
        this.selectedGhgRatingWeights = null;
        this.selectedVesselTypeEEDIPeeringGroups = null;
        this.ageFirstFilter = null;
        this.ageSecondFilter = null;
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.dwtApplied = false;
        this.ageApplied = false;
        this.clearVesselStatusFilter(false);

        this.reloadFilters();
    }

    @computedFrom(Reflection.getPropertyPath<CompanyGhgRating>(z => z.showSafetyRatingFilter),
    Reflection.getPropertyPath<CompanyGhgRating>(z => z.showGhgRatingFilter),
    Reflection.getPropertyPath<CompanyGhgRating>(z => z.showVesselTypeEEDIPeeringGroupFilter),
    Reflection.getPropertyPath<CompanyGhgRating>(z => z.showDwtFilter),
    Reflection.getPropertyPath<CompanyGhgRating>(z => z.showAgeFilter))
    public get mobileFilterSelected() {
        return this.showSafetyRatingFilter || this.showGhgRatingFilter || this.showVesselTypeEEDIPeeringGroupFilter
            || this.showDwtFilter || this.showAgeFilter;
    }

    //clear: clear filter value, reset: used for mobile to remove unapplied values, apply: apply the the filter values
    public selectedFilter: { title: string, clear: () => void, reset: () => void, apply: () => void };

    public goBackToFilters() {
        if (this.selectedFilter)
            this.selectedFilter.reset();
        this.closeAllFilters();
        this.selectedFilter = null;
    }

    onMobileApply() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
            this.closeAllFilters();
        } else {
            this.closeAllFilters();
            this.toggleMobileFilterDialog();
        }
    }

    //#region safety rating filter

    //safety rating filter
    public showSafetyRatingFilter: boolean = false;
    public selectedSafetyRatings: number[] = [];
    public mobileSafetyRatings: number[] = [];

    public toggleSafetyRatingFilters() {
        if (!this.showSafetyRatingFilter) {
            this.closeAllFilters();
        }
        this.showSafetyRatingFilter = !this.showSafetyRatingFilter;
        this.selectedFilter = {
            title: "Safety Score",
            clear: () => {
                this.clearSafetyRatingFilter();
            },
            reset: () => {
                this.mobileSafetyRatings = CloneHelper.cloneObject(this.selectedSafetyRatings);
            },
            apply: () => {
                this.applySafetyRatingFilter(this.mobileSafetyRatings.map(r => new RatingItem(true, r, r == -1 ? -1 : -3)));
            }
        }
    }

    public applySafetyRatingFilter(ratings: RatingItem[]) {
        this.selectedSafetyRatings = [];

        if (ratings && ratings.length > 0) {
            this.selectedSafetyRatings = this.selectedSafetyRatings.concat(ratings.map(x => x.rating));
        }

        this.reloadFilters();

        this.toggleSafetyRatingFilters();
    }

    public clearSafetyRatingFilter() {
        this.selectedSafetyRatings = [];
        this.mobileSafetyRatings = [];
        this.reloadFilters();
    }

    @computedFrom("selectedSafetyRatings", "selectedSafetyRatings.length")
    public get safetyRatingFilterActive() {
        return this.selectedSafetyRatings && this.selectedSafetyRatings.length > 0;
    }

    //#endregion

    //#region ghg rating
    public selectedGhgRatingWeights: number[] = [];
    public selectedGhgRatings: number[] = [];
    public mobileGhgRatingWeights: number[] = [];
    public showGhgRatingFilter: boolean = false;

    public toggleGhgRatingFilters() {
        if (!this.showGhgRatingFilter) {
            this.closeAllFilters();
        }
        this.showGhgRatingFilter = !this.showGhgRatingFilter;
        this.selectedFilter = {
            title: "GHG Rating",
            clear: () => {
                this.clearGhgRatingFilter();
            },
            reset: () => {
                this.mobileGhgRatingWeights = CloneHelper.cloneObject(this.selectedGhgRatingWeights);
            },
            apply: () => {
                this.applyGhgRating(this.mobileGhgRatingWeights);
            }
        }
    }

    public applyGhgRating(filters: number[]) {
        this.selectedGhgRatingWeights = filters;
        this.ratings = Enumerable.from(this.ghgRatingSelector.ratings).where(x => x.isSelected).select(x => x.rating).toArray();
        this.reloadFilters();
        this.toggleGhgRatingFilters();
    }

    public clearGhgRatingFilter() {
        this.selectedGhgRatingWeights = [];
        this.ratings = [];
        this.selectedGhgRatingWeights = [];
        this.mobileGhgRatingWeights = [];
        this.reloadFilters();
    }

    @computedFrom("selectedGhgRatingWeights", "selectedGhgRatingWeights.length")
    public get ghgRatingFilterActive() {
        return this.selectedGhgRatingWeights && this.selectedGhgRatingWeights.length > 0;
    }

    //#endregion

    //#region vessel type filter
    public selectedVesselTypeEEDIPeeringGroups: number[] = [];
    public mobileVesselTypeEEDIPeeringGroups: number[] = [];
    public showVesselTypeEEDIPeeringGroupFilter: boolean = false;

    public toggleVesselTypeEEDIPeeringGroupFilters() {

        if (!this.showVesselTypeEEDIPeeringGroupFilter) {
            this.closeAllFilters();
        }

        this.showVesselTypeEEDIPeeringGroupFilter = !this.showVesselTypeEEDIPeeringGroupFilter;
        this.selectedFilter = {
            title: "Vessel Type EEDI Peering Group",
            clear: () => {
                this.clearVesselTypeEEDIPeeringGroupFilter();
            },
            reset: () => {
                this.mobileVesselTypeEEDIPeeringGroups = CloneHelper.cloneObject(this.selectedVesselTypeEEDIPeeringGroups);
            },
            apply: () => {
                this.applyVesselTypeEEDIPeeringGroupFilter(this.mobileVesselTypeEEDIPeeringGroups.map(r => { return { id: r }; }));
            }
        }
    }

    public applyVesselTypeEEDIPeeringGroupFilter(items: any[]) {
        this.selectedVesselTypeEEDIPeeringGroups = items.map(x => x.id);
        this.reloadFilters();
        this.toggleVesselTypeEEDIPeeringGroupFilters();
    }

    public clearVesselTypeEEDIPeeringGroupFilter() {
        this.selectedVesselTypeEEDIPeeringGroups = [];
        this.mobileVesselTypeEEDIPeeringGroups = [];
        this.reloadFilters();
    }

    @computedFrom("selectedVesselTypeEEDIPeeringGroups", "selectedVesselTypeEEDIPeeringGroups.length")
    public get vesselTypeEEDIPeeringGroupFilterActive() {
        return this.selectedVesselTypeEEDIPeeringGroups && this.selectedVesselTypeEEDIPeeringGroups.length > 0;
    }

    //#endregion

    //#region dwt filter
    public dwtFirstFilter: number;
    public dwtSecondFilter: number;
    public mobileDwtFilter: { low: number, high: number };
    public showDwtFilter: boolean = false;
    public dwtApplied: boolean = false;

    public toggleDwtFilters() {
        if (!this.showDwtFilter) {
            this.closeAllFilters();
        }
        this.showDwtFilter = !this.showDwtFilter;
        this.selectedFilter = {
            title: "DWT",
            clear: () => {
                this.clearDwtFilter();
            },
            reset: () => {
                this.mobileDwtFilter = { low: CloneHelper.cloneObject(this.dwtFirstFilter), high: CloneHelper.cloneObject(this.dwtSecondFilter) };
            },
            apply: () => {
                this.applyDwtFilter(this.mobileDwtFilter);
            }
        }
    }

    public applyDwtFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.dwtFirstFilter = filterValue.low;
            this.dwtSecondFilter = filterValue.high
        }

        this.reloadFilters();
        this.toggleDwtFilters();
    }

    public clearDwtFilter() {
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.mobileDwtFilter = { low: null, high: null };
        this.dwtApplied = false;
        this.reloadFilters();
    }

    @computedFrom("dwtFirstFilter", "dwtSecondFilter", "dwtApplied")
    public get dwtFilterActive() {
        return this.dwtApplied && ((this.dwtFirstFilter && this.dwtFirstFilter >= 0) || (this.dwtSecondFilter && this.dwtSecondFilter <= 565000));
    }

    //#endregion

    //#region age filter

    public ageFirstFilter: number;
    public ageSecondFilter: number;
    public mobileAgeFilter: { low: number, high: number };
    public showAgeFilter: boolean = false;
    public ageApplied: boolean = false;

    public toggleAgeFilters() {
        if (!this.showAgeFilter) {
            this.closeAllFilters();
        }
        this.showAgeFilter = !this.showAgeFilter;
        this.selectedFilter = {
            title: "Age",
            clear: () => {
                this.clearAgeFilter();
            },
            reset: () => {
                this.mobileAgeFilter = { low: CloneHelper.cloneObject(this.ageFirstFilter), high: CloneHelper.cloneObject(this.ageSecondFilter) };
            },
            apply: () => {
                this.applyAgeFilter(this.mobileAgeFilter);
            }
        }
    }

    public applyAgeFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.ageFirstFilter = filterValue.low;
            this.ageSecondFilter = filterValue.high
        }

        this.reloadFilters();
        this.toggleAgeFilters();
    }

    public clearAgeFilter() {
        this.ageFirstFilter = null;
        this.ageSecondFilter = null;
        this.mobileAgeFilter = { low: null, high: null };
        this.ageApplied = false;
        this.reloadFilters();
    }

    @computedFrom("ageFirstFilter", "ageSecondFilter", "ageApplied")
    public get ageFilterActive() {
        return this.ageApplied && ((this.ageFirstFilter && this.ageFirstFilter >= 0) || (this.ageSecondFilter && this.ageSecondFilter <= 100));
    }

    //#endregion

    //#region Multi Checked List dialog filters
    public cachedMultipleLookups: { [lookup: string]: MultiLookupModel[] } = {};
    public mclFilters: { [lookup: string]: QueryFilter[] } = {};

    //#region vessel status filter
    public vesselStatusDisplayText: string = this.i18N.tr('vessel-status-text');
    public vesselStatusDescription: string = "";

    public clearVesselStatusFilter(reload: boolean = true) {
        const lookup = LookupValueQuery.VesselStatuses;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1)
            this.mclFilters[lookup] = [];
        this.vesselStatusDisplayText = "";
        this.vesselStatusDisplayText = this.i18N.tr('vessel-status-text');
        if (reload)
            this.reloadFilters();
    }

    @computedFrom("mclFilters", "mclFilters.RS_VesselStatuses", "mclFilters.RS_VesselStatuses.length")
    public get vesselStatusFilterActive() {
        const lookup = LookupValueQuery.VesselStatuses;
        return (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0);
    }
    //#endregion

    openMCLDialog = (type: string) => {
        let lookup = null;
        if (type == "vessel_types")
            lookup = LookupValueQuery.VesselTypes;
        else if (type == "vessel_statuses")
            lookup = LookupValueQuery.VesselStatuses;
        else {
            console.error("invalid multi filter type")
        }
        const displayText = type == "vessel_types" ? this.i18N.tr('vessel-type') : this.i18N.tr('vessel-status-text');

        this.closeAllFilters();

        const cachedMultiLookups = this.cachedMultipleLookups[lookup] ? this.cachedMultipleLookups[lookup] : RightShipMultiLookupModels.getMultiLookupModel(lookup);

        let filterValues: { [filterProperty: string]: number[] } = null;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0) {
            filterValues = {};
            this.mclFilters[lookup].forEach(x => {
                filterValues[x.filterProperty.name] = x.filterValue;
            });
        }

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.MultiCheckedListDialog, {
            multiLookups: cachedMultiLookups,
            title: displayText,
            selectedIds: filterValues,
            onApply: (multiLookups: MultiLookupModel[]) => {

                this.mclFilters[lookup] = [];

                const result = RightShipMultiLookupModels.getMultiLookupFilters(lookup, displayText, multiLookups);

                this.mclFilters[lookup] = result.filters;

                if (lookup == LookupValueQuery.VesselStatuses) {
                    this.vesselStatusDisplayText = result.filterDisplayText;
                    this.vesselStatusDescription = result.filterDescription;
                }

                this.cachedMultipleLookups[lookup] = multiLookups;

                this.reloadFilters();
            }
        }, true, false, false, 'right');
    }
    //#endregion

    //#region mobile filters
    isMobileFilterOpen: boolean = false;

    toggleMobileFilterDialog() {
        this.isMobileFilterOpen = !this.isMobileFilterOpen;
    }

    //#endregion

    public age(item: any) {
        if (item) {
            if (item.dateOfBuild) {
                return VesselHelper.getAgeFromDate(item.dateOfBuild);
            } else {
                return ""
            }
        } else {
            return null;
        }
    }

    public getColor(rating: string) {
        rating = rating.toLowerCase();
        if (rating == "a") {
            return "#00763B";
        } else if (rating == "b") {
            return "#AED36B";
        } else if (rating == "c") {
            return "#FFDD15";
        } else if (rating == "d") {
            return "#FFAA1C";
        } else if (rating == "e") {
            return "#D71920";
        } else if (rating == "u") {
            return "#42526E";
        }
    }

    public getBars(rating: string) {
        if (this.ratings.length > 0 && this.ratings.indexOf(rating) < 0) {
            return this.docVsWorldFadedItems;
        }

        return this.docVsWorldItems;
    }
}
